/**
 * Sets a cookie.
 * @param {string} name - Name for the cookie.
 * @param {string} value - Value for the cookie.
 * @param {number} days - Number of days until the cookie should expire.
 */
function setBannerCookie(name, value, days) {
  let expires = '';

  if (days) {
    const date = new Date();
    date.setTime(date.getTime() + (days * 24 * 60 * 60 * 1000));
    expires = `; expires=${date.toUTCString()}`;
  }

  document.cookie = `${encodeURIComponent(name)}=${encodeURIComponent(value)}${expires}; path=/; SameSite=Lax`;
}

/**
 * Gets the value of a cookie (if it exists).
 * @param {string} name - Name of the cookie.
 * @returns {?string}
 */
function getBannerCookie(name) {
  const cookieString = `; ${document.cookie}`;
  const cookies = cookieString.split(`; ${name}=`);

  if (cookies.length === 2) {
    return cookies.pop().split(';').shift();
  }

  return null;
}

if (!customElements.get('pop-up-banner')) {
  /**
   * A small, non-blocking promotional banner.
   * Unlike the theme's `modal-dialog`/`pop-up`, this element never covers the
   * full page, never locks body scroll, and never traps focus — the customer
   * can keep browsing the page underneath while it's visible.
   */
  class PopUpBanner extends HTMLElement {
    constructor() {
      super();
      this.cookieName = `${this.id}-dismissed`;
      this.reshowTrigger = this.dataset.reshowTrigger || 'time';
      this.escHandler = this.handleKeydown.bind(this);
    }

    connectedCallback() {
      this.addEventListener('click', (evt) => {
        if (evt.target.closest('.js-close-popup-banner')) this.close();
      });

      if (window.Shopify && window.Shopify.designMode) {
        document.addEventListener('shopify:section:select', (evt) => {
          if (evt.target === this.closest('.shopify-section')) this.open();
        });
        document.addEventListener('shopify:section:deselect', (evt) => {
          if (evt.target === this.closest('.shopify-section')) this.close();
        });
        return;
      }

      const mobileOk = this.dataset.showOnMobile === 'true' || (window.theme && window.theme.mediaMatches && window.theme.mediaMatches.md);

      if (!this.isDismissed() && mobileOk) {
        if (this.dataset.trigger === 'delay') {
          setTimeout(() => this.open(), Number(this.dataset.delay || 0) * 1000);
        } else if (this.dataset.trigger === 'exit' && window.theme && window.theme.mediaMatches && window.theme.mediaMatches.md) {
          this.mouseLeaveHandler = this.mouseLeaveHandler || this.handleMouseLeave.bind(this);
          document.body.addEventListener('mouseleave', this.mouseLeaveHandler);
        } else if (this.dataset.trigger === 'clipboard') {
          this.copyHandler = this.copyHandler || this.handleCopy.bind(this);
          document.addEventListener('copy', this.copyHandler);
        }
      }
    }

    disconnectedCallback() {
      if (this.mouseLeaveHandler) {
        document.body.removeEventListener('mouseleave', this.mouseLeaveHandler);
      }
      if (this.copyHandler) {
        document.removeEventListener('copy', this.copyHandler);
      }
      document.removeEventListener('keydown', this.escHandler);
    }

    /**
     * Determines whether this banner should stay hidden based on "Reshow control".
     * - page: never persisted, shows again on every page view
     * - session: persisted for the current browser tab session only
     * - time: persisted via cookie for a configurable number of hours
     * @returns {boolean}
     */
    isDismissed() {
      if (this.reshowTrigger === 'page') return false;
      if (this.reshowTrigger === 'session') return sessionStorage.getItem(this.cookieName) === 'true';
      return !!getBannerCookie(this.cookieName);
    }

    handleMouseLeave(evt) {
      if (evt.clientY < 0 && !this.isDismissed()) this.open();
    }

    handleCopy() {
      if (!this.isDismissed()) this.open();
    }

    handleKeydown(evt) {
      if (evt.key === 'Escape') this.close();
    }

    open() {
      this.classList.remove('invisible');
      this.setAttribute('aria-hidden', 'false');
      document.addEventListener('keydown', this.escHandler);
    }

    close() {
      this.classList.add('invisible');
      this.setAttribute('aria-hidden', 'true');
      document.removeEventListener('keydown', this.escHandler);

      if (this.reshowTrigger === 'session') {
        sessionStorage.setItem(this.cookieName, 'true');
      } else if (this.reshowTrigger === 'time') {
        const hours = Number(this.dataset.reshowHours) || 720;
        setBannerCookie(this.cookieName, true, hours / 24);
      }
      // reshowTrigger === 'page' の場合は何も保存しない(次回ページ表示で必ず再表示)
    }
  }

  customElements.define('pop-up-banner', PopUpBanner);
}

/**
 * 商品ページの sticky-atc-panel（画面外からせり上がってくる「カートに追加」バー）の
 * 表示状態・高さを監視し、その分だけポップアップバナーが連動して迫り上がるように
 * CSSカスタムプロパティ --sticky-atc-offset を更新する。
 * ポップアップバナー自体が存在しないページでも害はないので、常に動かしておく。
 */
(function observeStickyAtcPanel() {
  var GAP = 12; // sticky-atc-panelとバナーの間の隙間(px)
  var watching = false;
  var currentPanel = null;
  var ticking = false;

  function isPanelVisible(panel) {
    // ページ下部まで到達すると body に scrolled-to-bottom が付き、
    // sticky-atc-panel自身のクラス(--out)とは別に非表示になる。
    if (document.body.classList.contains('scrolled-to-bottom')) return false;
    return !panel.classList.contains('sticky-atc-panel--out');
  }

  function apply(panel) {
    var visible = isPanelVisible(panel);
    var height = visible ? panel.getBoundingClientRect().height : 0;
    var offset = height > 0 ? Math.round(height + GAP) : 0;
    document.documentElement.style.setProperty('--sticky-atc-offset', offset + 'px');
  }

  function recheck() {
    if (currentPanel && !currentPanel.isConnected) {
      // 何らかの理由で要素が差し替えられていた場合に備え、最新のものを取得し直す
      currentPanel = document.querySelector('sticky-atc-panel');
    }
    if (currentPanel) apply(currentPanel);
  }

  function onScroll() {
    if (ticking) return;
    ticking = true;
    window.requestAnimationFrame(function () {
      recheck();
      ticking = false;
    });
  }

  function watch(panel) {
    if (watching) return;
    watching = true;
    currentPanel = panel;

    apply(panel);

    // クラスの付け外し(表示/非表示の切り替え)を検知
    var classObserver = new MutationObserver(function () {
      apply(panel);
    });
    classObserver.observe(panel, { attributes: true, attributeFilter: ['class'] });

    // body側のクラス変化(scrolled-to-bottomの付与など)も即座に検知
    var bodyObserver = new MutationObserver(function () {
      apply(panel);
    });
    bodyObserver.observe(document.body, { attributes: true, attributeFilter: ['class'] });

    // パネル自体の高さが変わった場合(文言の折り返しなど)も追従
    if ('ResizeObserver' in window) {
      var resizeObserver = new ResizeObserver(function () {
        apply(panel);
      });
      resizeObserver.observe(panel);
    }

    // 保険1: MutationObserverがクラス変化を取りこぼした場合に備え、
    // スクロール中は継続的に再チェックして自己修復する
    window.addEventListener('scroll', onScroll, { passive: true });

    // 保険2: スクロールが止まった後に遅れてクラスが変わるケースなど、
    // イベントベースの検知を取りこぼしても最終的に必ず正しい状態に補正されるよう、
    // 一定間隔で強制的に再チェックする(コストの軽い処理なので負荷は無視できるレベル)
    setInterval(recheck, 400);
  }

  function init() {
    document.documentElement.style.setProperty('--sticky-atc-offset', '0px');

    var existingPanel = document.querySelector('sticky-atc-panel');
    if (existingPanel) {
      watch(existingPanel);
      return;
    }

    // まだDOMに存在しない場合、後から追加されるのを待つ
    var insertObserver = new MutationObserver(function () {
      var panel = document.querySelector('sticky-atc-panel');
      if (panel) {
        insertObserver.disconnect();
        watch(panel);
      }
    });
    insertObserver.observe(document.body, { childList: true, subtree: true });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
